import React, { useState, useEffect, useRef } from 'react';
import { useUpload } from '../contexts/UploadContext';
import {
  UploadCloud,
  FileSpreadsheet,
  FileText,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Clock,
  Loader2,
  RefreshCw,
  Sliders,
  Cpu,
  BarChart2,
  Layers,
  Database,
  Zap,
  X,
  Download,
  Info,
  ArrowRight,
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.port === '3000' ? `http://${window.location.hostname}:8000` : (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000'));

interface StageInfo {
  label: string;
  detail: string;
  icon: React.ComponentType<{ className?: string }>;
}

const STAGE_CONFIG: Record<string, StageInfo> = {
  parsing: {
    label: 'Parsing File',
    detail: 'Loading and decoding dataset schema...',
    icon: FileSpreadsheet,
  },
  standardizing_data: {
    label: 'Standardizing Data',
    detail: 'Normalizing column names and formats...',
    icon: RefreshCw,
  },
  parsing_features: {
    label: 'Parsing Features',
    detail: 'Extracting dates, services, and numeric features...',
    icon: Sliders,
  },
  building_features: {
    label: 'Building Feature Vectors',
    detail: 'Computing derived financial signals and ratios...',
    icon: Layers,
  },
  layer1_hard_rules: {
    label: 'Layer 1 · Hard Rules',
    detail: 'Scanning for policy limits, benefit caps, and duplicates...',
    icon: ShieldCheck,
  },
  layer2_statistical_analysis: {
    label: 'Layer 2 · Statistical Analysis',
    detail: 'Detecting anomalous billing distributions...',
    icon: BarChart2,
  },
  layer3_anomaly_detection: {
    label: 'Layer 3 · AI Anomaly Isolation',
    detail: 'Executing Isolation Forest on multi-dimensional vectors...',
    icon: Cpu,
  },
  fuzzy_logic_combiner: {
    label: 'Fuzzy Logic Combiner',
    detail: 'Synthesizing multi-layer risk scores...',
    icon: Layers,
  },
  saving: {
    label: 'Persisting Claims',
    detail: 'Bulk indexing records into database...',
    icon: Database,
  },
  precomputing_kpis: {
    label: 'Precomputing KPIs',
    detail: 'Generating cached analytics for instant response...',
    icon: Zap,
  },
  completed: {
    label: 'Analysis Complete',
    detail: 'All processing finished! Dashboard is synchronized.',
    icon: CheckCircle2,
  },
};

interface FileUploadProps {
  onUploadComplete?: (mode?: 'claims' | 'policies' | 'overview') => void;
  defaultMode?: 'claims' | 'policies';
}

const FileUpload: React.FC<FileUploadProps> = ({ onUploadComplete, defaultMode = 'claims' }) => {
  const {
    uploading,
    uploadMode,
    setUploadMode,
    fileName,
    currentStage,
    currentPercent,
    stageLog,
    uploadResult,
    error,
    elapsed,
    startClaimsUpload,
    startPoliciesUpload,
    resetUpload,
    setError,
  } = useUpload();

  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const logEndRef = useRef<HTMLDivElement>(null);
  const prevDefaultModeRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (defaultMode && defaultMode !== prevDefaultModeRef.current && !uploading) {
      prevDefaultModeRef.current = defaultMode;
      setUploadMode(defaultMode);
    }
  }, [defaultMode, uploading, setUploadMode]);

  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [stageLog]);

  const wasUploadingRef = useRef(false);

  useEffect(() => {
    if (uploading) {
      wasUploadingRef.current = true;
    }
    if (wasUploadingRef.current && !uploading && uploadResult) {
      wasUploadingRef.current = false;
      if (onUploadComplete) {
        onUploadComplete(uploadResult.is_bulk_policy ? 'policies' : 'claims');
      }
    }
  }, [uploading, uploadResult, onUploadComplete]);

  const fmtElapsed = (ms: number) => {
    const s = Math.floor(ms / 1000);
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (selected && selected.length > 0) {
      const fileList = Array.from(selected);
      if (uploadMode === 'policies') {
        const ok = fileList.every(f => {
          const name = f.name.toLowerCase();
          return name.endsWith('.pdf') || name.endsWith('.zip');
        });
        if (!ok) {
          setError('Please upload only PDF or ZIP policy files.');
          return;
        }
      } else {
        const ok = fileList.every(f => {
          const name = f.name.toLowerCase();
          return (
            name.endsWith('.xlsx') ||
            name.endsWith('.xls') ||
            name.endsWith('.csv') ||
            name.endsWith('.json') ||
            name.endsWith('.xml')
          );
        });
        if (!ok) {
          setError('Please upload only Excel, CSV, JSON, or XML claims files.');
          return;
        }
        if (fileList.length > 1) {
          setError('Only one claims dataset can be processed per batch.');
          return;
        }
      }
      setFiles(fileList);
      setError(null);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const dropped = e.dataTransfer.files;
    if (dropped && dropped.length > 0) {
      const fileList = Array.from(dropped);
      if (uploadMode === 'policies') {
        const ok = fileList.every(f => {
          const name = f.name.toLowerCase();
          return name.endsWith('.pdf') || name.endsWith('.zip');
        });
        if (!ok) {
          setError('Please upload only PDF or ZIP policy files.');
          return;
        }
      } else {
        const ok = fileList.every(f => {
          const name = f.name.toLowerCase();
          return (
            name.endsWith('.xlsx') ||
            name.endsWith('.xls') ||
            name.endsWith('.csv') ||
            name.endsWith('.json') ||
            name.endsWith('.xml')
          );
        });
        if (!ok) {
          setError('Please upload only Excel, CSV, JSON, or XML claims files.');
          return;
        }
        if (fileList.length > 1) {
          setError('Only one claims dataset can be processed per batch.');
          return;
        }
      }
      setFiles(fileList);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    const isPdfsOrZip = files.every(f => {
      const name = f.name.toLowerCase();
      return name.endsWith('.pdf') || name.endsWith('.zip');
    });

    if (uploadMode === 'policies' && isPdfsOrZip) {
      await startPoliciesUpload(files);
      setFiles([]);
    } else {
      await startClaimsUpload(files[0]);
      setFiles([]);
    }
  };

  const clearFile = () => {
    setFiles([]);
    resetUpload();
    const fi = document.getElementById('file-upload') as HTMLInputElement;
    if (fi) fi.value = '';
  };

  const handleModeSwitch = (mode: 'claims' | 'policies') => {
    if (uploading || uploadMode === mode) return;
    setFiles([]);
    const fi = document.getElementById('file-upload') as HTMLInputElement;
    if (fi) fi.value = '';
    resetUpload();
    setUploadMode(mode);
  };

  const ActiveStageIcon = currentStage && STAGE_CONFIG[currentStage] ? STAGE_CONFIG[currentStage].icon : Loader2;

  return (
    <div className="w-full max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Mode Selector Tabs */}
      <div className="flex items-center justify-between flex-wrap gap-4 border-b border-slate-200 dark:border-slate-800 pb-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <UploadCloud className="w-6 h-6 text-primary-600" />
            Ingestion & AI Analysis Center
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Process healthcare datasets and insurance policies through the multi-layer fraud engine
          </p>
        </div>

        <div className="flex p-1 bg-slate-100 dark:bg-slate-800/80 rounded-xl border border-slate-200/80 dark:border-slate-700/60">
          <button
            type="button"
            onClick={() => handleModeSwitch('claims')}
            disabled={uploading}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
              uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            } ${
              uploadMode === 'claims'
                ? 'bg-white dark:bg-slate-900 text-primary-600 dark:text-primary-400 shadow-sm border border-slate-200/60 dark:border-slate-700/60'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />
            <span>Claims Dataset</span>
          </button>
          <button
            type="button"
            onClick={() => handleModeSwitch('policies')}
            disabled={uploading}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
              uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            } ${
              uploadMode === 'policies'
                ? 'bg-white dark:bg-slate-900 text-rose-600 dark:text-rose-400 shadow-sm border border-slate-200/60 dark:border-slate-700/60'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            <span>Policy Guides (OCR)</span>
          </button>
        </div>
      </div>

      {/* Supported Badges */}
      {!uploading && !uploadResult && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-slate-500 mr-1">Supported formats:</span>
          {uploadMode === 'claims' ? (
            <>
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                .xlsx / .xls
              </span>
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200/60">
                .csv
              </span>
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200/60">
                .json
              </span>
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-purple-50 text-purple-700 border border-purple-200/60">
                .xml
              </span>
            </>
          ) : (
            <>
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200/60">
                .pdf (Multi-page OCR)
              </span>
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-cyan-50 text-cyan-700 border border-cyan-200/60">
                .zip (Bulk Policies)
              </span>
            </>
          )}
        </div>
      )}

      {/* Drop Zone Area - only visible when not currently processing and no result */}
      {!uploading && !uploadResult && (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => files.length === 0 && document.getElementById('file-upload')?.click()}
          className={`relative rounded-2xl border-2 border-dashed p-8 text-center transition-all duration-200 cursor-pointer ${
            dragActive
              ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-950/20 scale-[1.01]'
              : files.length > 0
              ? 'border-emerald-500 bg-emerald-50/30 dark:bg-emerald-950/10'
              : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900/50 hover:border-primary-400 hover:bg-slate-50/50 dark:hover:bg-slate-900'
          }`}
        >
          <input
            id="file-upload"
            type="file"
            multiple={uploadMode === 'policies'}
            accept={uploadMode === 'policies' ? '.pdf,.zip' : '.xlsx,.xls,.csv,.json,.xml'}
            className="hidden"
            onChange={handleFileSelect}
          />

          {files.length === 0 ? (
            <div className="flex flex-col items-center justify-center space-y-3">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-primary-600 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-primary-500/20">
                <UploadCloud className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800 dark:text-white">
                  {dragActive ? 'Release file to upload' : 'Click to browse or drag & drop files'}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {uploadMode === 'policies'
                    ? 'Upload PDF benefit booklets or ZIP archives for automated rule extraction'
                    : 'Upload medical claims files for deep multi-layer fraud & statistical screening'}
                </p>
              </div>
              <button
                type="button"
                className="px-4 py-2 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-semibold text-xs shadow-sm hover:opacity-95 transition-opacity"
              >
                Select Files
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-4 p-2">
              <div className="flex items-center gap-3.5 text-left min-w-0">
                <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-950/60 border border-primary-300 text-primary-600 flex items-center justify-center shrink-0">
                  <FileSpreadsheet className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <h4 className="text-sm font-bold text-slate-900 dark:text-white truncate">
                    {files.length === 1 ? files[0].name : `${files.length} policy files selected`}
                  </h4>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-slate-500 font-mono">
                      {(files.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(2)} MB
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-800">
                      <CheckCircle2 className="w-3 h-3" /> Ready
                    </span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  clearFile();
                }}
                className="p-2 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
                title="Remove file"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Action Trigger Button */}
      {files.length > 0 && !uploading && !uploadResult && (
        <button
          type="button"
          onClick={handleUpload}
          className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl bg-gradient-to-r from-blue-600 via-primary-600 to-indigo-700 hover:from-blue-500 hover:to-indigo-600 text-white font-bold text-sm shadow-md shadow-primary-600/30 transition-all cursor-pointer"
        >
          <ShieldCheck className="w-4 h-4" />
          <span>
            {uploadMode === 'policies'
              ? 'Execute OCR Policy Limits Extraction'
              : 'Launch Multi-Layer AI Fraud Analysis'}
          </span>
          <ArrowRight className="w-4 h-4" />
        </button>
      )}

      {/* Real-time Analysis Progress Card */}
      {uploading && (
        <div className="rounded-2xl bg-slate-950 text-white p-6 shadow-xl border border-slate-800 space-y-5 animate-fade-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold tracking-tight">Real-Time AI Pipeline</span>
                {fileName && <span className="text-xs text-slate-400 font-mono truncate max-w-[220px]">({fileName})</span>}
              </div>
            </div>

            <div className="flex items-center gap-3 text-xs font-mono">
              <span className="text-slate-400 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {fmtElapsed(elapsed)}
              </span>
              <span className="text-primary-400 font-bold text-sm">{currentPercent}%</span>
            </div>
          </div>

          {/* Progress Bar with Glow */}
          <div className="h-2 w-full bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 via-primary-500 to-emerald-400 rounded-full transition-all duration-300 shadow-[0_0_12px_rgba(59,130,246,0.5)]"
              style={{ width: `${currentPercent}%` }}
            />
          </div>

          {/* Active Stage Hero */}
          {currentStage && STAGE_CONFIG[currentStage] && (
            <div className="flex items-center gap-3.5 p-3.5 rounded-xl bg-slate-900 border border-slate-800">
              <div className="p-2 rounded-lg bg-primary-600/20 text-primary-400">
                <ActiveStageIcon className="w-5 h-5 animate-pulse" />
              </div>
              <div className="flex-1 min-w-0">
                <h5 className="text-xs font-bold text-slate-100">{STAGE_CONFIG[currentStage].label}</h5>
                <p className="text-[11px] text-slate-400 truncate">{STAGE_CONFIG[currentStage].detail}</p>
              </div>
              <Loader2 className="w-4 h-4 text-primary-400 animate-spin shrink-0" />
            </div>
          )}

          {/* Execution Log */}
          <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
            {stageLog.map((entry, idx) => {
              const meta = STAGE_CONFIG[entry.stage] || {
                label: entry.stage,
                detail: '',
                icon: CheckCircle2,
              };
              const StepIcon = meta.icon;
              const isCompleted = idx < stageLog.length - 1;
              return (
                <div
                  key={idx}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                    isCompleted
                      ? 'bg-emerald-950/30 border-emerald-800/40 text-emerald-300'
                      : 'bg-primary-950/40 border-primary-800/40 text-primary-200'
                  }`}
                >
                  <div className="flex items-center gap-2 truncate">
                    {isCompleted ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    ) : (
                      <StepIcon className="w-3.5 h-3.5 text-primary-400 shrink-0 animate-spin" />
                    )}
                    <span className="truncate">{meta.label}</span>
                  </div>
                  <span className="font-mono text-[11px] font-bold shrink-0">{entry.percent}%</span>
                </div>
              );
            })}
            <div ref={logEndRef} />
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-rose-800 dark:text-rose-300 animate-fade-in">
          <AlertCircle className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-xs font-bold tracking-wide">Processing Interrupted</h4>
            <p className="text-xs mt-0.5">{error}</p>
          </div>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-rose-500 hover:text-rose-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Success Summary Dashboard Card */}
      {uploadResult && (
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 p-6 shadow-xl space-y-6 animate-fade-in">
          <div className="flex items-center gap-3.5 pb-4 border-b border-slate-100 dark:border-slate-800">
            <div className="w-12 h-12 rounded-2xl bg-emerald-100 dark:bg-emerald-950/60 border border-emerald-300 text-emerald-600 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">Analysis & Ingestion Succeeded</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-mono truncate">{uploadResult.filename}</p>
            </div>
          </div>

          {/* Results Grid */}
          {uploadResult.is_bulk_policy ? (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-cyan-50 dark:bg-cyan-950/30 border border-cyan-200 dark:border-cyan-800 flex items-center justify-between">
                <div>
                  <span className="text-[11px] font-bold text-cyan-800 dark:text-cyan-300">
                    Total Extracted Benefit Clauses
                  </span>
                  <p className="text-2xl font-extrabold text-cyan-900 dark:text-cyan-100 font-mono mt-0.5">
                    {uploadResult.records_processed?.toLocaleString()}
                  </p>
                </div>
                <FileText className="w-8 h-8 text-cyan-600" />
              </div>
            </div>
          ) : (
            uploadResult.fraud_summary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3.5 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                  <span className="text-[10px] font-bold text-blue-700 dark:text-blue-300">
                    Records Evaluated
                  </span>
                  <p className="text-xl font-extrabold text-blue-900 dark:text-blue-100 font-mono mt-1">
                    {(uploadResult.records_processed || uploadResult.fraud_summary.total_claims)?.toLocaleString()}
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800">
                  <span className="text-[10px] font-bold text-rose-700 dark:text-rose-300">
                    Calculated Fraud Rate
                  </span>
                  <p className="text-xl font-extrabold text-rose-900 dark:text-rose-100 font-mono mt-1">
                    {uploadResult.fraud_summary.fraud_rate}%
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                  <span className="text-[10px] font-bold text-amber-700 dark:text-amber-300">
                    Flagged Anomalies
                  </span>
                  <p className="text-xl font-extrabold text-amber-900 dark:text-amber-100 font-mono mt-1">
                    {uploadResult.fraud_summary.fraud_claims?.toLocaleString()}
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800">
                  <span className="text-[10px] font-bold text-purple-700 dark:text-purple-300">
                    Duplicate Claims
                  </span>
                  <p className="text-xl font-extrabold text-purple-900 dark:text-purple-100 font-mono mt-1">
                    {uploadResult.fraud_summary.duplicate_claims?.toLocaleString()}
                  </p>
                </div>
              </div>
            )
          )}

          {/* Action Row */}
          <div className="flex items-center gap-3 pt-2 flex-wrap sm:flex-nowrap">
            <button
              type="button"
              onClick={clearFile}
              className="flex-1 py-2.5 px-4 rounded-xl border border-slate-300 dark:border-slate-700 font-semibold text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer text-center"
            >
              Upload Additional Data
            </button>

            {uploadResult.processed_file && (
              <a
                href={`${API_BASE_URL}/${uploadResult.processed_file}`}
                download
                className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-md shadow-emerald-600/20 transition-all cursor-pointer text-center"
              >
                <Download className="w-4 h-4" />
                <span>Download Annotated Dataset</span>
              </a>
            )}

            {onUploadComplete && uploadResult.is_bulk_policy && (
              <button
                type="button"
                onClick={() => onUploadComplete('policies')}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-xs shadow-md shadow-cyan-600/20 transition-all cursor-pointer text-center"
              >
                <FileText className="w-4 h-4" />
                <span>View Policy Benefits</span>
              </button>
            )}

            {onUploadComplete && (
              <button
                type="button"
                onClick={() => onUploadComplete('overview')}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-primary-600 hover:bg-primary-500 text-white font-bold text-xs shadow-md shadow-primary-600/20 transition-all cursor-pointer text-center"
              >
                <BarChart2 className="w-4 h-4" />
                <span>View Dashboard Analytics</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-950/30 p-3 rounded-xl border border-primary-200/60 dark:border-primary-800/40">
            <Info className="w-4 h-4 shrink-0" />
            <span>Dashboard metrics have updated automatically. Navigate to the Overview or Fraud tabs to explore insights.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;

